import { formatCardRef } from '../log/logDisplay.js';
/**
 * Renders a reveal predicate to human-readable text.
 *
 * @param predicate The reveal predicate to describe.
 * @returns A short phrase, e.g. `"cost ≤ 3"` or `"always"`.
 */
export function describeRevealPredicate(predicate) {
    if (predicate.kind === 'always') {
        return 'always';
    }
    if (predicate.kind === 'cost-zero') {
        return 'cost is 0';
    }
    if (predicate.kind === 'cost-odd') {
        return 'cost is odd';
    }
    if (predicate.kind === 'cost-lte') {
        // why: a threshold of 0 is legitimate (reveal M=0), so test undefined explicitly
        // rather than a falsy fallback — mirrors revealPredicateMatches.
        return predicate.threshold === undefined
            ? 'cost ≤ (no threshold)'
            : `cost ≤ ${predicate.threshold}`;
    }
    if (predicate.kind === 'cost-gte') {
        return predicate.threshold === undefined
            ? 'cost ≥ (no threshold)'
            : `cost ≥ ${predicate.threshold}`;
    }
    // why: WP-729 / D-24550 — name the reveal trait criterion for the outcome log line
    // ("an instinct card" / "a hydra card"). Article-aware (a vowel-initial slug takes
    // "an"), consistent with the WP-729 Part B X-Gene message fix.
    if (predicate.kind === 'team' || predicate.kind === 'hero-class') {
        const traitValue = predicate.traitValue ?? 'matching';
        const article = /^[aeiou]/i.test(traitValue) ? 'an' : 'a';
        return `${article} ${traitValue} card`;
    }
    return `an unrecognized predicate (${String(predicate.kind)})`;
}
/**
 * Renders one reveal action to a human-readable outcome phrase.
 *
 * @param action The reveal action.
 * @param cost The revealed card's cost (the attack-by-cost amount).
 * @returns A short phrase, e.g. `"drew it"` or `"gained +6 attack"`.
 */
function describeRevealAction(action, cost) {
    const kind = action.kind;
    if (kind === 'draw') {
        return 'drew it';
    }
    if (kind === 'ko') {
        return "KO'd it";
    }
    if (kind === 'discard') {
        return 'discarded it';
    }
    // why: D-24670 — name the amount; "gained attack" hid how much the reveal granted
    // (High Stakes Jackpot, match iNKIWCJu8FH).
    if (kind === 'attack-by-cost') {
        return `gained +${cost} attack`;
    }
    if (kind === 'attack-fixed') {
        if (action.amount === undefined) {
            return 'gained attack';
        }
        return `gained +${action.amount} attack`;
    }
    if (kind === 'choose-discard-or-return') {
        return 'queued a choice';
    }
    return 'applied an unrecognized action';
}
/**
 * Renders a matched rule's actions to a comma-joined phrase.
 *
 * @param actions The matched rule's actions, in order.
 * @param cost The revealed card's cost (the attack-by-cost amount).
 * @returns The joined phrase, e.g. `"gained +4 attack, queued a choice"`.
 */
export function describeRevealActions(actions, cost) {
    const phrases = [];
    for (const action of actions) {
        phrases.push(describeRevealAction(action, cost));
    }
    return phrases.join(', ');
}
/**
 * Renders one reveal action kind as the THING it was supposed to do, for the
 * WP-417 "matched but did not apply" clause.
 *
 * @param kind The reveal action kind.
 * @returns A short noun phrase, e.g. `"the draw"`.
 */
function describeUnappliedRevealActionKind(kind) {
    if (kind === 'draw') {
        return 'the draw';
    }
    if (kind === 'ko') {
        return 'the KO';
    }
    if (kind === 'discard') {
        return 'the discard';
    }
    if (kind === 'attack-by-cost' || kind === 'attack-fixed') {
        return 'the attack grant';
    }
    if (kind === 'choose-discard-or-return') {
        return 'the choice';
    }
    return 'an unrecognized action';
}
/**
 * Renders the matched actions that failed to realize their mutation.
 *
 * WP-417 / D-24237 — `describeRevealActions` states what the matched branch
 * CLAIMED ("drew it"); this states what of it did not actually happen, so a
 * guard-blocked action (an empty deck, a missing turnEconomy) is no longer
 * reported to the player as a success.
 *
 * @param kinds The matched action kinds that returned "did not apply", in order.
 * @returns The joined phrase, e.g. `"the KO"`, or an empty string when none failed.
 */
export function describeUnappliedRevealActions(kinds) {
    const phrases = [];
    for (const kind of kinds) {
        phrases.push(describeUnappliedRevealActionKind(kind));
    }
    return phrases.join(', ');
}
/**
 * Builds the reveal-outcome game-log line for one peeked card.
 *
 * @param cardDisplayData The `G.cardDisplayData` snapshot, or undefined.
 * @param playerID The revealing player's id.
 * @param revealedCardId The peeked deck-top card's ext-id.
 * @param cost The peeked card's cost (the predicate input).
 * @param outcome The composed reveal outcome (matched + predicate/action text).
 * @returns The formatted line.
 */
export function formatRevealOutcomeLine(cardDisplayData, playerID, revealedCardId, cost, outcome) {
    const cardRef = formatCardRef(cardDisplayData, revealedCardId);
    if (!outcome.matched) {
        return `Player ${playerID} revealed ${cardRef} (cost ${cost}) — no branch matched (left on top).`;
    }
    // why: WP-417 / D-24237 — a matched branch whose action was guard-blocked used to
    // print the success phrase anyway ("drew it" with nothing drawn). Name the gap.
    if (outcome.unappliedActionsText !== undefined && outcome.unappliedActionsText.length > 0) {
        return `Player ${playerID} revealed ${cardRef} (cost ${cost}) — ${outcome.predicateText} matched: ${outcome.actionsText}, but ${outcome.unappliedActionsText} could not be applied.`;
    }
    return `Player ${playerID} revealed ${cardRef} (cost ${cost}) — ${outcome.predicateText} matched: ${outcome.actionsText}.`;
}
//# sourceMappingURL=revealLog.js.map